'use client';
import 'react-multi-carousel/lib/styles.css';

import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import Carousel from 'react-multi-carousel';

import type { TMedia } from '~constants/media';
import { ImageRenderer } from '~shared/ui/molecules';

import {
	GridCarouselContainer,
	GridCarouselItem,
	GridCarouselPaginationItem,
} from './styles';

type TCarouselProps = {
	items?: TMedia[][];
	fallbacks: string[];
};

export const GridCarousel = ({
	items = [],
	fallbacks = [],
}: TCarouselProps) => {
	const [hovering, setHovering] = useState(false);
	const carouselRef = useRef<Carousel>(null);
	const [forwardAnimation, setForwardAnimation] = useState(true);
	const length = items.length || fallbacks.length;
	const imgs = new Array(length).fill(0);
	useEffect(() => {
		if (isMobile) return;
		if (typeof window === 'undefined') return;
		let interval: number | undefined;

		if (hovering) {
			if (imgs.length === 1) return;
			interval = setInterval(() => {
				const activePageIndex = carouselRef.current?.state?.currentSlide || 0;

				let index = forwardAnimation
					? activePageIndex + 1
					: activePageIndex - 1;
				if (index >= length) {
					setForwardAnimation(false);
					index = activePageIndex - 1;
				} else if (index < 0) {
					setForwardAnimation(true);
					index = 1;
				}
				carouselRef.current?.goToSlide(index);
			}, 1000) as unknown as number;
		}

		return () => {
			interval && clearInterval(interval);
		};
	}, [hovering, length, forwardAnimation, imgs.length]);

	const responsive = {
		all: {
			items: 1,
			breakpoint: { max: Number.MAX_VALUE, min: 0 },
		},
	};

	return (
		<GridCarouselContainer
			onMouseEnter={() => setHovering(true)}
			onMouseLeave={() => setHovering(false)}
		>
			<Carousel
				draggable={false}
				swipeable={true}
				ssr={false}
				arrows={false}
				keyBoardControl={false}
				showDots={true}
				ref={carouselRef}
				customDot={<GridCarouselPaginationItem />}
				renderDotsOutside={false}
				autoPlaySpeed={1000}
				pauseOnHover={false}
				minimumTouchDrag={20}
				autoPlay={false}
				infinite={false}
				rewind={true}
				rewindWithAnimation={true}
				responsive={responsive}
			>
				{imgs.map((item, index) => (
					<GridCarouselItem key={index} isLast={index + 1 === imgs.length}>
						<ImageRenderer
							item={items[index]}
							fallback={fallbacks[index]}
							width={300}
							height={400}
							quality={85}
							showLowQualityPreview={true}
							alt=""
						/>
					</GridCarouselItem>
				))}
			</Carousel>
		</GridCarouselContainer>
	);
};
